import { type FormEvent, type ReactNode, useCallback, useState } from "react";
import { Link, Navigate, useNavigate, useOutletContext, useParams } from "react-router";
import { DUE_FILTERS, type DueFilter, LABEL_COLORS, type LabelDTO, type SavedFilterDTO, type TaskDTO, type TaskFilter, type UserDTO } from "@justone/shared";
import { errorMessage, fieldError } from "../api/http";
import { useChannels, useUserMap, useUsers } from "../api/queries";
import { useFilterMutations, useFilters, useLabelEvents, useLabelMutations, useLabels, useListMutations, useLists, useTaskSearch } from "../api/tasks";
import { QuickAdd, type QuickAddDefaults } from "../components/QuickAdd";
import { TaskRow } from "../components/TaskBits";
import { Dialog, EmptyState, Field, Icon, type IconName, Skeleton, useConfirm, useDismiss, useToast } from "../components/ui";
import { useMenuKeys } from "../components/Popover";
import { formatDay, formatTime } from "../lib/format";
import { formatDayHeading, PRIORITY_LABELS, today, VIEW_FILTERS } from "../lib/tasks";
import type { WorkspaceOutlet } from "./Workspace";
import { PageShell } from "./OtherPages";

/* ───────────── 공통: 할일 목록 화면 ───────────── */

interface Group {
  key: string;
  title: string;
  tone?: "overdue";
  tasks: TaskDTO[];
}

/** 하위 할일은 상위 할일이 같은 목록에 있으면 그 밑에 들여 쓴다 */
function nest(tasks: TaskDTO[]): { task: TaskDTO; depth: number }[] {
  const ids = new Set(tasks.map((t) => t.id));
  const kids = new Map<number, TaskDTO[]>();
  const roots: TaskDTO[] = [];
  for (const t of tasks) {
    if (t.parentId !== null && ids.has(t.parentId)) kids.set(t.parentId, [...(kids.get(t.parentId) ?? []), t]);
    else roots.push(t);
  }
  const out: { task: TaskDTO; depth: number }[] = [];
  const walk = (t: TaskDTO, depth: number) => {
    out.push({ task: t, depth });
    for (const k of kids.get(t.id) ?? []) walk(k, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return out;
}

function TaskGroup({ group, users, showPlace }: { group: Group; users: Map<number, UserDTO>; showPlace: boolean }) {
  return (
    <section className="task-group" aria-label={group.title}>
      {group.title && <h2 className={group.tone === "overdue" ? "tone-overdue" : undefined}>{group.title} <span className="count">{group.tasks.length}</span></h2>}
      <div className="task-list">
        {nest(group.tasks).map(({ task, depth }) => (
          <TaskRow key={task.id} task={task} users={users} showPlace={showPlace} depth={depth} />
        ))}
      </div>
    </section>
  );
}

function TaskView({
  title,
  filter,
  group = (tasks) => [{ key: "all", title: "", tasks }],
  addDefaults,
  actions,
  empty,
  emptyIcon = "check",
  showPlace = true,
  allowDone = true,
  intro,
}: {
  title: string;
  filter: TaskFilter;
  group?: (tasks: TaskDTO[]) => Group[];
  addDefaults?: QuickAddDefaults | null;
  actions?: ReactNode;
  empty: { title: string; body?: ReactNode };
  emptyIcon?: IconName;
  showPlace?: boolean;
  allowDone?: boolean;
  intro?: ReactNode;
}) {
  const [showDone, setShowDone] = useState(false);
  const q = useTaskSearch(filter);
  const done = useTaskSearch({ ...filter, includeDone: true }, showDone);
  const users = useUserMap();
  const tasks = q.data ?? [];
  const groups = group(tasks).filter((g) => g.tasks.length > 0);
  const finished = (done.data ?? []).filter((t) => t.status === "done");
  return (
    <PageShell
      title={title}
      actions={
        <>
          {actions}
          {allowDone && (
            <label className="qa-option switch-label">
              <input type="checkbox" role="switch" className="switch" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
              완료한 할일
            </label>
          )}
        </>
      }
    >
      {intro}
      {addDefaults !== null && <QuickAdd inline defaults={addDefaults} placeholder="할일 추가 · 예: 금요일 오후 3시 보고서 +급함 !2" />}
      {q.isPending ? (
        <Skeleton kind="list" rows={6} />
      ) : q.isError ? (
        <p className="empty">불러오지 못했습니다. {errorMessage(q.error)}</p>
      ) : groups.length === 0 ? (
        <EmptyState icon={emptyIcon} title={empty.title}>
          {empty.body}
        </EmptyState>
      ) : (
        groups.map((g) => <TaskGroup key={g.key} group={g} users={users} showPlace={showPlace} />)
      )}
      {showDone && finished.length > 0 && <TaskGroup group={{ key: "done", title: "완료", tasks: finished }} users={users} showPlace={showPlace} />}
    </PageShell>
  );
}

/* ───────────── 받은함·오늘·다가오는 ───────────── */

export function InboxPage() {
  return <TaskView title="받은함" filter={VIEW_FILTERS.inbox} addDefaults={{}} showPlace={false} emptyIcon="inbox" empty={{ title: "받은함이 비었습니다", body: "생각나는 일은 위 입력창이나 어디서든 Q를 눌러 적어 두세요." }} />;
}

export function TodayPage() {
  const d = today();
  return (
    <TaskView
      title="오늘"
      filter={VIEW_FILTERS.today}
      addDefaults={{ dueDate: d }}
      allowDone={false}
      group={(tasks) => [
        { key: "overdue", title: "기한 지남", tone: "overdue", tasks: tasks.filter((t) => t.dueDate! < d) },
        { key: "today", title: formatDayHeading(d), tasks: tasks.filter((t) => t.dueDate === d) },
      ]}
      empty={{ title: "오늘 할 일을 모두 끝냈습니다", body: <Link to="/upcoming">다가오는 할일 보기</Link> }}
      intro={<p className="view-intro">오늘까지인 내 할일과 나에게 맡겨진 팀 할일입니다.</p>}
    />
  );
}

export function UpcomingPage() {
  const d = today();
  return (
    <TaskView
      title="다가오는"
      filter={VIEW_FILTERS.upcoming}
      addDefaults={{}}
      allowDone={false}
      group={(tasks) => {
        const groups: Group[] = [{ key: "overdue", title: "기한 지남", tone: "overdue", tasks: tasks.filter((t) => t.dueDate! < d) }];
        for (const t of tasks.filter((x) => x.dueDate! >= d)) {
          const last = groups.at(-1)!;
          if (last.key === t.dueDate) last.tasks.push(t);
          else groups.push({ key: t.dueDate!, title: formatDayHeading(t.dueDate!), tasks: [t] });
        }
        return groups;
      }}
      empty={{ title: "마감이 정해진 할일이 없습니다", body: "할일에 '내일'이나 '금요일 오후 3시'처럼 날짜를 붙이면 여기 날짜별로 모입니다." }}
    />
  );
}

/* ───────────── 개인 목록 ───────────── */

export function ListPage() {
  const { listId } = useParams();
  const id = Number(listId);
  const { data: lists } = useLists();
  const list = lists?.find((l) => l.id === id);
  const ops = useListMutations();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [renaming, setRenaming] = useState(false);
  if (lists && !list) return <Navigate to="/inbox" replace />;
  if (!list) return null;
  const remove = async () => {
    if (!(await confirm({ title: `목록 "${list.name}" 삭제`, body: "안에 있는 할일은 지워지지 않고 받은함으로 옮겨집니다.", confirmLabel: "목록 삭제", danger: true }))) return;
    ops.remove.mutate(list.id, { onSuccess: () => navigate("/inbox"), onError: (e) => toast(errorMessage(e)) });
  };
  return (
    <>
      <TaskView
        key={list.id}
        title={list.name}
        filter={{ scope: "mine", lists: [list.id] }}
        addDefaults={{ listId: list.id }}
        showPlace={false}
        empty={{ title: `목록 "${list.name}"이(가) 비었습니다`, body: `할일에 #${list.name}을(를) 붙이면 이 목록에 들어옵니다.` }}
        actions={
          <MoreMenu label="목록 메뉴">
            <button type="button" role="menuitem" onClick={() => setRenaming(true)}>
              이름 바꾸기
            </button>
            <button type="button" role="menuitem" className="btn-danger" onClick={remove}>
              목록 지우기
            </button>
          </MoreMenu>
        }
      />
      {renaming && <NameDialog title="목록 이름 바꾸기" initial={list.name} hint="공백 없이. 빠른 입력에서 #이름으로 찾습니다." onClose={() => setRenaming(false)} onSave={(name) => ops.update.mutateAsync({ id: list.id, name })} />}
    </>
  );
}

function MoreMenu({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss<HTMLDivElement>(open, close);
  useMenuKeys(open, ref);
  return (
    <div ref={ref} onClick={(e) => (e.target as HTMLElement).closest("[role=menuitem]") && close()}>
      <button type="button" className="icon-btn" style={{ color: "var(--ink-2)" }} onClick={() => setOpen((o) => !o)} aria-label={label} aria-haspopup="menu" aria-expanded={open}>
        <Icon name="more" />
      </button>
      {open && (
        <div className="menu" role="menu">
          {children}
        </div>
      )}
    </div>
  );
}

export function NameDialog({ title, initial = "", hint, onClose, onSave }: { title: string; initial?: string; hint?: string; onClose: () => void; onSave: (name: string) => Promise<unknown> }) {
  const [name, setName] = useState(initial);
  const [err, setErr] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await onSave(name);
      onClose();
    } catch (e) {
      setErr(e);
    }
  };
  return (
    <Dialog open onClose={onClose} label={title}>
      <form onSubmit={submit}>
        <h2>{title}</h2>
        <Field id="name-dialog" label="이름" hint={hint} error={err ? (fieldError(err, "name") ?? errorMessage(err)) : undefined}>
          <input id="name-dialog" className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoFocus />
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary">
            저장
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ───────────── 라벨 ───────────── */

const COLOR_NAMES: Record<string, string> = { slate: "회색", red: "빨강", orange: "주황", amber: "노랑", green: "초록", teal: "청록", blue: "파랑", violet: "보라", pink: "분홍" };

export function LabelsPage() {
  const { data: labels = [] } = useLabels();
  const events = useLabelEvents();
  const users = useUserMap();
  const ops = useLabelMutations();
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [scope, setScope] = useState<LabelDTO["scope"]>("shared");
  const [color, setColor] = useState<string>("slate");
  const [err, setErr] = useState<unknown>(null);
  const [renaming, setRenaming] = useState<LabelDTO | null>(null);

  const create = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    ops.create.mutate({ name, scope, color }, { onSuccess: () => setName(""), onError: setErr });
  };
  const remove = async (l: LabelDTO) => {
    const ok = await confirm({
      title: `라벨 "${l.name}" 삭제`,
      body: `이 라벨이 붙은 할일에서 떨어집니다.${l.scope === "shared" ? " 공용 라벨이라 모두에게서 사라집니다." : ""}`,
      confirmLabel: "라벨 삭제",
      danger: true,
    });
    if (ok) ops.remove.mutate(l.id, { onError: (e) => toast(errorMessage(e)) });
  };
  const section = (title: string, list: LabelDTO[], note: string) => (
    <>
      <h2>{title}</h2>
      <p className="view-intro">{note}</p>
      {list.length === 0 ? (
        <p className="empty">아직 없습니다.</p>
      ) : (
        <div className="list">
          {list.map((l) => (
            <div key={l.id} className="list-row">
              <span className={`label-dot lc-${l.color}`} aria-hidden="true" />
              <Link to={`/labels/${l.id}`} className="grow title">
                {l.name}
              </Link>
              <select className="input input-sm" value={l.color} aria-label={`${l.name} 색`} onChange={(e) => ops.update.mutate({ id: l.id, color: e.target.value }, { onError: (er) => toast(errorMessage(er)) })}>
                {LABEL_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {COLOR_NAMES[c]}
                  </option>
                ))}
              </select>
              <button type="button" className="btn btn-sm" onClick={() => setRenaming(l)}>
                이름 바꾸기
              </button>
              <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(l)}>
                지우기
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <PageShell title="라벨">
      <form className="inline-form label-form" onSubmit={create}>
        <Field id="label-name" label="새 라벨" error={err ? (fieldError(err, "name") ?? errorMessage(err)) : undefined}>
          <input id="label-name" className="input" required maxLength={30} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 급함" autoComplete="off" />
        </Field>
        <select className="input" value={scope} onChange={(e) => setScope(e.target.value as LabelDTO["scope"])} aria-label="공개 범위">
          <option value="shared">공용 (모두에게 보임)</option>
          <option value="personal">개인 (나만)</option>
        </select>
        <select className="input" value={color} onChange={(e) => setColor(e.target.value)} aria-label="색">
          {LABEL_COLORS.map((c) => (
            <option key={c} value={c}>
              {COLOR_NAMES[c]}
            </option>
          ))}
        </select>
        <button type="submit" className="btn" disabled={ops.create.isPending}>
          만들기
        </button>
      </form>
      {section("공용 라벨", labels.filter((l) => l.scope === "shared"), "팀 할일과 개인 할일 모두에 붙일 수 있고, 누구나 고칠 수 있습니다. 고친 기록은 아래에 남습니다.")}
      {section("내 라벨", labels.filter((l) => l.scope === "personal"), "내 개인 할일에만 붙고 나만 봅니다.")}
      <h2>공용 라벨 변경 기록</h2>
      {(events.data ?? []).length === 0 ? (
        <p className="empty">아직 없습니다.</p>
      ) : (
        <ul className="history-list">
          {events.data!.map((e) => (
            <li key={e.id}>
              <span>
                {users.get(e.actorId ?? 0)?.displayName ?? "알 수 없음"}님이{" "}
                {e.action === "create" ? `"${e.name}"을(를) 만들었습니다` : e.action === "rename" ? `"${e.before}"을(를) "${e.name}"(으)로 바꿨습니다` : e.action === "recolor" ? `"${e.name}"의 색을 바꿨습니다` : `"${e.name}"을(를) 지웠습니다`}
              </span>
              <span className="muted">
                {formatDay(e.createdAt)} {formatTime(e.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {renaming && <NameDialog title="라벨 이름 바꾸기" initial={renaming.name} hint="공백 없이. 빠른 입력에서 +이름으로 찾습니다." onClose={() => setRenaming(null)} onSave={(n) => ops.update.mutateAsync({ id: renaming.id, name: n })} />}
    </PageShell>
  );
}

export function LabelPage() {
  const { labelId } = useParams();
  const id = Number(labelId);
  const { data: labels } = useLabels();
  const label = labels?.find((l) => l.id === id);
  if (labels && !label) return <Navigate to="/labels" replace />;
  if (!label) return null;
  return (
    <TaskView
      key={label.id}
      title={`+${label.name}`}
      filter={{ scope: "all", labelIds: [label.id] }}
      addDefaults={{ labelIds: [label.id] }}
      empty={{ title: `라벨 +${label.name}이(가) 붙은 할일이 없습니다` }}
      actions={
        <Link to="/labels" className="btn btn-sm">
          라벨 관리
        </Link>
      }
    />
  );
}

/* ───────────── 저장한 필터 ───────────── */

export function FilterPage() {
  const { filterId } = useParams();
  const id = Number(filterId);
  const { data: filters } = useFilters();
  const f = filters?.find((x) => x.id === id);
  const ops = useFilterMutations();
  const navigate = useNavigate();
  const { go } = useOutletContext<WorkspaceOutlet>();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const describe = useDescribeFilter();
  if (filters && !f) return <Navigate to="/today" replace />;
  if (!f) return null;
  // 필터는 이름과 조건만이라 되돌리기로 다시 만든다
  const remove = () => {
    const { name, conditions } = f;
    ops.remove.mutate(f.id, {
      onSuccess: () => {
        navigate("/today");
        toast(`필터 "${name}"을(를) 지웠습니다.`, {
          label: "되돌리기",
          run: () =>
            ops.create
              .mutateAsync({ name, conditions })
              .then((made) => go(`/filters/${made.id}`))
              .catch((e) => toast(errorMessage(e))),
        });
      },
      onError: (e) => toast(errorMessage(e)),
    });
  };
  return (
    <>
      <TaskView
        key={`${f.id}-${JSON.stringify(f.conditions)}`}
        title={f.name}
        filter={f.conditions}
        addDefaults={null}
        empty={{ title: "조건에 맞는 할일이 없습니다", body: "필터를 고쳐 조건을 넓혀 보세요." }}
        intro={<p className="view-intro">{describe(f.conditions)}</p>}
        actions={
          <MoreMenu label="필터 메뉴">
            <button type="button" role="menuitem" onClick={() => setEditing(true)}>
              조건 고치기
            </button>
            <button type="button" role="menuitem" className="btn-danger" onClick={remove}>
              필터 지우기
            </button>
          </MoreMenu>
        }
      />
      {editing && <FilterDialog filter={f} onClose={() => setEditing(false)} />}
    </>
  );
}

const DUE_NAMES: Record<DueFilter, string> = {
  overdue: "기한 지남",
  today: "오늘",
  through_today: "오늘까지 (지난 것 포함)",
  this_week: "이번 주",
  next_7: "앞으로 7일",
  has_date: "마감 있음",
  none: "마감 없음",
};

function useDescribeFilter() {
  const { data: lists = [] } = useLists();
  const { data: channels = [] } = useChannels();
  const { data: labels = [] } = useLabels();
  const users = useUserMap();
  return (c: TaskFilter) => {
    const parts = [c.scope === "all" ? "내 할일과 모든 팀 할일" : "내 할일과 맡은 팀 할일"];
    const places = [...(c.lists ?? []).map((l) => (l === "inbox" ? "받은함" : (lists.find((x) => x.id === l)?.name ?? "?"))), ...(c.channelIds ?? []).map((id) => `#${channels.find((x) => x.id === id)?.name ?? "?"}`)];
    if (places.length) parts.push(places.join(", "));
    if (c.labelIds?.length) parts.push(c.labelIds.map((id) => `+${labels.find((l) => l.id === id)?.name ?? "?"}`).join(", "));
    if (c.assignees?.length) parts.push(`담당: ${c.assignees.map((a) => (a === "me" ? "나" : a === "none" ? "없음" : (users.get(a)?.displayName ?? "?"))).join(", ")}`);
    if (c.due) parts.push(DUE_NAMES[c.due]);
    if (c.priorities?.length) parts.push(`우선순위 ${c.priorities.map((p) => PRIORITY_LABELS[p]).join(", ")}`);
    if (c.recurring !== undefined) parts.push(c.recurring ? "반복만" : "반복 아님");
    return parts.join(" · ");
  };
}

/** 조건 빌더 (새 필터 또는 고치기) */
export function FilterDialog({ filter, onClose }: { filter?: SavedFilterDTO; onClose: () => void }) {
  const [name, setName] = useState(filter?.name ?? "");
  const [c, setC] = useState<TaskFilter>(filter?.conditions ?? { scope: "mine" });
  const [err, setErr] = useState<unknown>(null);
  const { data: lists = [] } = useLists();
  const { data: channels = [] } = useChannels();
  const { data: labels = [] } = useLabels();
  const { data: users = [] } = useUsers();
  const ops = useFilterMutations();
  const navigate = useNavigate();
  const preview = useTaskSearch(c);
  const describe = useDescribeFilter();

  const toggle = <T,>(key: "lists" | "channelIds" | "labelIds" | "assignees" | "priorities", v: T) =>
    setC((cur) => {
      const list = ((cur[key] as T[] | undefined) ?? []).slice();
      const i = list.indexOf(v);
      if (i >= 0) list.splice(i, 1);
      else list.push(v);
      return { ...cur, [key]: list.length ? list : undefined };
    });
  const has = <T,>(key: "lists" | "channelIds" | "labelIds" | "assignees" | "priorities", v: T) => ((c[key] as T[] | undefined) ?? []).includes(v);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (filter) await ops.update.mutateAsync({ id: filter.id, name, conditions: c });
      else navigate(`/filters/${(await ops.create.mutateAsync({ name, conditions: c })).id}`);
      onClose();
    } catch (e) {
      setErr(e);
    }
  };

  const box = (key: Parameters<typeof toggle>[0], value: number | string, label: string) => (
    <label key={`${key}-${value}`} className="check-pill">
      <input type="checkbox" checked={has(key, value)} onChange={() => toggle(key, value)} />
      {label}
    </label>
  );

  return (
    <Dialog open onClose={onClose} label={filter ? "필터 고치기" : "새 필터"}>
      <form onSubmit={submit} className="filter-form">
        <h2>{filter ? "필터 고치기" : "새 필터"}</h2>
        {err && !fieldError(err, "name") ? <p className="form-error">{errorMessage(err)}</p> : null}
        <Field id="filter-name" label="이름" error={fieldError(err, "name")}>
          <input id="filter-name" className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 이번 주 급한 일" autoComplete="off" autoFocus />
        </Field>
        <fieldset>
          <legend>범위</legend>
          <label className="check-pill">
            <input type="radio" name="scope" checked={c.scope === "mine"} onChange={() => setC({ ...c, scope: "mine" })} />내 할일 + 나에게 맡겨진 팀 할일
          </label>
          <label className="check-pill">
            <input type="radio" name="scope" checked={c.scope === "all"} onChange={() => setC({ ...c, scope: "all" })} />내 할일 + 모든 팀 할일
          </label>
        </fieldset>
        <fieldset>
          <legend>목록·채널 (고르지 않으면 전부)</legend>
          {box("lists", "inbox", "받은함")}
          {lists.map((l) => box("lists", l.id, l.name))}
          {channels.filter((ch) => ch.joined).map((ch) => box("channelIds", ch.id, `#${ch.name}`))}
        </fieldset>
        {labels.length > 0 && (
          <fieldset>
            <legend>라벨 (하나라도 붙은 것)</legend>
            {labels.map((l) => box("labelIds", l.id, `+${l.name}`))}
          </fieldset>
        )}
        <fieldset>
          <legend>담당자</legend>
          {box("assignees", "me", "나")}
          {box("assignees", "none", "담당자 없음")}
          {users.filter((u) => u.active).map((u) => box("assignees", u.id, u.displayName))}
        </fieldset>
        <fieldset>
          <legend>마감</legend>
          <select className="input input-sm" value={c.due ?? ""} onChange={(e) => setC({ ...c, due: (e.target.value || undefined) as DueFilter | undefined })} aria-label="마감">
            <option value="">상관없음</option>
            {DUE_FILTERS.map((d) => (
              <option key={d} value={d}>
                {DUE_NAMES[d]}
              </option>
            ))}
          </select>
        </fieldset>
        <fieldset>
          <legend>우선순위</legend>
          {[1, 2, 3, 4].map((p) => box("priorities", p, PRIORITY_LABELS[p]!))}
        </fieldset>
        <fieldset>
          <legend>반복</legend>
          <select className="input input-sm" value={c.recurring === undefined ? "" : c.recurring ? "yes" : "no"} onChange={(e) => setC({ ...c, recurring: e.target.value === "" ? undefined : e.target.value === "yes" })} aria-label="반복">
            <option value="">상관없음</option>
            <option value="yes">반복 할일만</option>
            <option value="no">반복 아닌 것만</option>
          </select>
        </fieldset>
        <p className="view-intro">
          {describe(c)} → {preview.data ? `${preview.data.length}개` : "…"}
        </p>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary">
            저장
          </button>
        </div>
      </form>
    </Dialog>
  );
}
